/**
 * Modal for creating/editing folders.
 */
import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button, Input } from '@/components/ui';
import { useCreateFolder } from '@/hooks/queries/useMediaQueries';
import type { MediaFolder } from '@/types/media';

interface FolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folders: MediaFolder[];
  parentFolderId?: string | null;
}

export function FolderModal({
  isOpen,
  onClose,
  folders,
  parentFolderId = null,
}: FolderModalProps) {
  const [name, setName] = useState('');
  const [selectedParentId, setSelectedParentId] = useState<string>(parentFolderId || '');
  const [error, setError] = useState('');
  const createMutation = useCreateFolder();

  // Reset form when opening
  useEffect(() => {
    if (isOpen) {
      setName('');
      setSelectedParentId(parentFolderId || '');
      setError('');
    }
  }, [isOpen, parentFolderId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Folder name is required');
      return;
    }

    if (name.includes('/')) {
      setError('Folder name cannot contain "/"');
      return;
    }

    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        parent_folder_id: selectedParentId || undefined,
      });
      onClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create folder';
      setError(errorMessage);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Create Folder</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit}>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Folder Name
                </label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My Folder"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Folder (optional)
                </label>
                <select
                  value={selectedParentId}
                  onChange={(e) => setSelectedParentId(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Root (No parent)</option>
                  {folders.map((folder) => (
                    <option key={folder.id} value={folder.id}>
                      {folder.path}
                    </option>
                  ))}
                </select>
              </div>

              {error && (
                <p className="text-sm text-red-600">{error}</p>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
              <Button variant="secondary" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating...' : 'Create Folder'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
